import {ChangeDetectorRef, Component, DestroyRef, Input, OnInit} from '@angular/core'
import {UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators} from '@angular/forms'
import {StaticComponentInstance, StaticFormLayoutConfig} from '../../static-form/static-form.types'
import {FormControls, TypedFormControls, TypedFormGroup} from '../../../../../../core/forms/form-controls.model'
import {filter, pairwise, startWith} from 'rxjs/operators'
import {
  InstallLamps_ConnectionProximity,
  InstallLamps_LampWeight,
  InstallLamps_RoomCeiling,
  InstallLamps_TypeOfLamps,
  InstallLamps_WallMaterial,
  InstallLampsStaticJobQuestions,
  InstallLampsStaticJobQuestionsInput,
  StaticJobQuestionsInput,
} from '../../../../../../../__generated__/types'
import {atLeastOneOptionSelected} from '../../../../../../core/forms/at-least-one-selected.validator'
import {takeUntilDestroyed} from '@angular/core/rxjs-interop'
import {BehaviorSubject, Observable} from 'rxjs'

type WallMaterial = Omit<InstallLamps_WallMaterial, '__typename'>
type TypeOfLamps = Omit<InstallLamps_TypeOfLamps, '__typename'>
type ConnectionProximity = Omit<InstallLamps_ConnectionProximity, '__typename'>
type ExtraInfo = {optionsForm: Record<string, unknown>}

@Component({
  selector: 'bgo-install-lamps-static-form',
  templateUrl: './install-lamps-static-form.component.html',
  styleUrls: ['../../gradual-reveal.css'],
})
export class InstallLampsStaticFormComponent
  implements StaticComponentInstance<InstallLampsStaticJobQuestionsInput, ExtraInfo>, OnInit
{
  @Input() form!: TypedFormGroup<InstallLampsStaticJobQuestionsInput>

  readonly lampWeight = InstallLamps_LampWeight
  readonly ceilingHeight = InstallLamps_RoomCeiling

  readonly lampTypeOptionControls: TypedFormControls<TypeOfLamps> = {
    ceiling: new UntypedFormControl(),
    hanging: new UntypedFormControl(),
    wall: new UntypedFormControl(),
    chandelier: new UntypedFormControl(),
    pendant: new UntypedFormControl(),
    other: new UntypedFormControl(),
  }
  readonly wallMaterialOptionControls: TypedFormControls<WallMaterial> = {
    concrete: new UntypedFormControl(),
    plaster: new UntypedFormControl(),
    wood: new UntypedFormControl(),
    metal: new UntypedFormControl(),
    brick: new UntypedFormControl(),
    unknown: new UntypedFormControl(),
  }
  readonly connectionProximityOptionControls: TypedFormControls<ConnectionProximity> = {
    directlyAtConnection: new UntypedFormControl(),
    upToOneMeter: new UntypedFormControl(),
    moreThanOneMeter: new UntypedFormControl(),
  }
  readonly electricalConnectionOptionConrols = {
    electricalConnectionOption: new UntypedFormControl(undefined, [Validators.required]),
  }
  readonly optionControls = {
    lampTypeOptions: new UntypedFormGroup(this.lampTypeOptionControls, [atLeastOneOptionSelected()]),
    lampTypeSingleSelect: new UntypedFormControl(),
    wallMaterialOptions: new UntypedFormGroup(this.wallMaterialOptionControls, [atLeastOneOptionSelected()]),
    wallMaterialSingleSelect: new UntypedFormControl(),
    electricalConnectionOptions: new UntypedFormGroup(this.electricalConnectionOptionConrols),
    connectionProximityOptions: new UntypedFormGroup(this.connectionProximityOptionControls, [
      atLeastOneOptionSelected(),
    ]),
    connectionProximitySingleSelect: new UntypedFormControl(),
  }
  readonly optionsForm = new UntypedFormGroup(this.optionControls)

  private readonly _layout$ = new BehaviorSubject({showBreakdown: true, numberOfItems: 0})

  constructor(
    private readonly cdr: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {}

  get lampTypeCount(): number {
    return InstallLampsStaticFormComponent.getOptionCount(this.lampTypeControls)
  }

  get wallMaterialCount(): number {
    return InstallLampsStaticFormComponent.getOptionCount(this.wallMaterialControls)
  }

  get connectionProximityCount(): number {
    return InstallLampsStaticFormComponent.getOptionCount(this.connectionProximityControls)
  }

  get lampTypeForm(): UntypedFormGroup {
    return this.form.controls.lampTypes as UntypedFormGroup
  }

  get lampTypeControls(): FormControls {
    return this.lampTypeForm.controls
  }

  get wallMaterialForm(): UntypedFormGroup {
    return this.form.controls.wallMaterial as UntypedFormGroup
  }

  get wallMaterialControls(): FormControls {
    return this.wallMaterialForm.controls
  }

  get connectionProximityForm(): UntypedFormGroup {
    return this.form.controls.connectionProximity as UntypedFormGroup
  }

  get connectionProximityControls(): FormControls {
    return this.connectionProximityForm.controls
  }

  get numberOfLamps(): number {
    return Number.parseInt(this.form.controls.numberOfLamps.value)
  }

  public static createForm(): TypedFormGroup<InstallLampsStaticJobQuestionsInput> {
    const lampTypeControls: TypedFormControls<TypeOfLamps> = {
      ceiling: new UntypedFormControl(),
      hanging: new UntypedFormControl(),
      wall: new UntypedFormControl(),
      chandelier: new UntypedFormControl(),
      pendant: new UntypedFormControl(),
      other: new UntypedFormControl(),
    }
    const wallMaterialControls: TypedFormControls<WallMaterial> = {
      concrete: new UntypedFormControl(),
      plaster: new UntypedFormControl(),
      wood: new UntypedFormControl(),
      metal: new UntypedFormControl(),
      brick: new UntypedFormControl(),
      unknown: new UntypedFormControl(),
    }
    const connectionProximityControls: TypedFormControls<ConnectionProximity> = {
      directlyAtConnection: new UntypedFormControl(),
      upToOneMeter: new UntypedFormControl(),
      moreThanOneMeter: new UntypedFormControl(),
    }
    const numberOfLamps = new UntypedFormControl(undefined, [Validators.required])
    const controls: TypedFormControls<Omit<InstallLampsStaticJobQuestions, '__typename'>> = {
      numberOfLamps,
      lampTypes: new UntypedFormGroup(lampTypeControls, [
        InstallLampsStaticFormComponent.allLampsAccountedFor(numberOfLamps),
      ]),
      wallMaterial: new UntypedFormGroup(wallMaterialControls, [
        InstallLampsStaticFormComponent.allLampsAccountedFor(numberOfLamps),
      ]),
      electricalConnections: new UntypedFormControl(undefined, [Validators.required]),
      connectionProximity: new UntypedFormGroup(connectionProximityControls, [
        InstallLampsStaticFormComponent.allLampsAccountedFor(numberOfLamps),
      ]),
      heaviestLampWeight: new UntypedFormControl(undefined, [Validators.required]),
      highestRoomCeiling: new UntypedFormControl(undefined, [Validators.required]),
      disposalRequired: new UntypedFormControl(undefined, [Validators.required]),
    }

    return new TypedFormGroup<InstallLampsStaticJobQuestions>(controls)
  }

  private static allLampsAccountedFor(numberOfLampsControl: UntypedFormControl): ValidatorFn {
    return (control): ValidationErrors | null => {
      if (
        InstallLampsStaticFormComponent.getOptionCount((control as UntypedFormGroup).controls) !==
        numberOfLampsControl.value
      ) {
        return {notAllLampsAccountedFor: true}
      }

      return null
    }
  }

  private static getOptionCount(controls: FormControls): number {
    return Object.values(controls)
      .filter(v => !!v?.value)
      .map(v => Number.parseInt(v.value))
      .reduce((a, b) => a + b, 0)
  }

  ngOnInit(): void {
    this.observeNumberOfLamps()
    this.observeAndSetOptionState()
    this.observeElectricalConnectionOption()
    this.observeSingleSelectionControls()
    this.observeAndResetValues()
  }

  layout$(): Observable<StaticFormLayoutConfig> {
    return this._layout$.asObservable()
  }

  questionsInput(): StaticJobQuestionsInput {
    return {
      installLamps: {
        ...this.form?.value,
        numberOfLamps: this.form?.value.numberOfLamps ?? 0,
        lampTypes: this.form?.value.lampTypes ?? {},
        wallMaterial: this.form?.value.wallMaterial ?? {},
        connectionProximity: this.form?.value.connectionProximity ?? {},
        electricalConnections: this.form?.value.electricalConnections ?? 1,
        disposalRequired: this.form?.value.disposalRequired ?? false,
        heaviestLampWeight: this.form?.value.heaviestLampWeight ?? InstallLamps_LampWeight.Under_10Kg,
        highestRoomCeiling: this.form?.value.highestRoomCeiling ?? InstallLamps_RoomCeiling.Under_2_4M,
      },
    }
  }

  hydrateExtraInfo(additionalInfo: ExtraInfo) {
    if (additionalInfo.optionsForm) {
      this.optionsForm.patchValue(additionalInfo.optionsForm)
    }
  }

  saveExtraInfo() {
    return {
      optionsForm: this.optionsForm.getRawValue(),
    }
  }

  markAllAsTouched(): void {
    this.form.markAllAsTouched()
    this.optionsForm.markAllAsTouched()
  }

  /**
   * Observe value changes to multi-select options, and enable/disable the options when total quantity matches the
   * number of lamps.
   */
  private observeAndSetOptionState(): void {
    this.form.controls.lampTypes.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkLampTypeOptions())

    this.form.controls.wallMaterial.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkWallMaterialOptions())

    this.form.controls.connectionProximity.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.checkConnectionProximityOptions())
  }

  /**
   * When we deselect an option, set the counts to undefined
   */
  private observeAndResetValues(): void {
    const resetOptions = (options: FormControls, controls: FormControls) => {
      Object.entries(options)
        .filter(([, v]) => !v.value)
        .forEach(([k, v]) => {
          controls[k].setValue(undefined)
        })
    }

    this.optionControls.lampTypeOptions.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => resetOptions(this.lampTypeOptionControls, this.lampTypeControls))

    this.optionControls.wallMaterialOptions.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => resetOptions(this.wallMaterialOptionControls, this.wallMaterialControls))

    this.optionControls.connectionProximityOptions.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => resetOptions(this.connectionProximityOptionControls, this.connectionProximityControls))
  }

  /**
   * Electrical connection is a number, but user is only able to select yes or no. When selecting yes set the number
   * to however many lamps there are, else 0
   */
  private observeElectricalConnectionOption() {
    this.electricalConnectionOptionConrols.electricalConnectionOption.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(value => {
        if (value === 'yes') {
          this.form.controls.electricalConnections.setValue(this.form.controls.numberOfLamps.value)
        } else if (value === 'no' || value === 'unknown') {
          this.form.controls.electricalConnections.setValue(0)
        }
      })
  }

  /**
   * When number of lamps change, we need to do some important stuff
   * @private
   */
  private observeNumberOfLamps(): void {
    this.form.controls.numberOfLamps.valueChanges
      .pipe(
        //do it twice to force the first emission with "pairwise()"
        startWith(this.form.controls.numberOfLamps.value as string),
        startWith(this.form.controls.numberOfLamps.value as string),
        pairwise(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(([oldValue, newValue]) => {
        if (oldValue && oldValue !== newValue) {
          this.form.reset({
            numberOfLamps: newValue,
          })
          this.optionsForm.reset()
          this._layout$.next({numberOfItems: newValue, showBreakdown: true})
        }
      })
  }

  private checkLampTypeOptions() {
    if (this.numberOfLamps > 1 && this.lampTypeCount === this.numberOfLamps) {
      this.disableOptionControls(this.lampTypeOptionControls)
    } else {
      this.enableOptionControls(this.lampTypeOptionControls)
    }
  }

  private checkWallMaterialOptions() {
    if (this.numberOfLamps > 1 && this.wallMaterialCount === this.numberOfLamps) {
      this.disableOptionControls(this.wallMaterialOptionControls)
    } else {
      this.enableOptionControls(this.wallMaterialOptionControls)
    }
  }

  private checkConnectionProximityOptions() {
    if (this.numberOfLamps > 1 && this.connectionProximityCount === this.numberOfLamps) {
      this.disableOptionControls(this.connectionProximityOptionControls)
    } else {
      this.enableOptionControls(this.connectionProximityOptionControls)
    }
  }

  private disableOptionControls(controls: FormControls): void {
    Object.values(controls)
      .filter(v => !v.value)
      .forEach(v => {
        v.disable({emitEvent: false})
      })

    this.cdr.detectChanges()
  }

  private enableOptionControls(controls: FormControls): void {
    Object.values(controls).forEach(v => {
      v.enable({emitEvent: false})
    })

    this.cdr.detectChanges()
  }

  private observeSingleSelectionControls() {
    const setSingleSelectionDefaults = (controls: FormControls, value: string) => {
      Object.values(controls).forEach(c => c.setValue(undefined, {emitEvent: false}))
      controls[value].setValue(1)
    }

    this.optionControls.lampTypeSingleSelect.valueChanges
      .pipe(
        filter(v => !!v),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(v => setSingleSelectionDefaults(this.lampTypeControls, v))

    this.optionControls.wallMaterialSingleSelect.valueChanges
      .pipe(
        filter(v => !!v),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(v => {
        setSingleSelectionDefaults(this.wallMaterialControls, v)
      })

    this.optionControls.connectionProximitySingleSelect.valueChanges
      .pipe(
        filter(v => !!v),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(v => {
        setSingleSelectionDefaults(this.connectionProximityControls, v)
      })
  }
}
